/* Lazy, section-scoped 3D viewer. Model and runtime are bundled theme assets. */
(() => {
  if (customElements.get('hy-knit-viewer')) return;
  let runtime;
  class KnitViewer extends HTMLElement {
    connectedCallback() {
      if (this.abort) return;
      this.abort = new AbortController();
      const options = { signal: this.abort.signal };
      this.model = this.querySelector('model-viewer');
      this.status = this.querySelector('[data-model-status]');
      this.controls = this.querySelector('.knit-viewer-controls');
      this.toggle = this.querySelector('[data-model-action="toggle"]');
      this.retry = this.querySelector('.knit-viewer-retry');
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.rotating = this.dataset.autoRotate === 'true' && !this.motion.matches;
      this.model.addEventListener('load', () => {
        this.ready = true;
        this.loading = false;
        this.classList.add('is-ready');
        this.controls.hidden = false;
        this.retry.hidden = true;
        this.status.textContent = 'Drag to rotate · 360°';
        this.syncRotation();
      }, options);
      this.model.addEventListener('error', () => this.fail(), options);
      this.model.addEventListener('pointerdown', () => this.pause(), options);
      this.model.addEventListener('keydown', e => {
        if (['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(e.key)) this.pause();
      }, options);
      this.controls.addEventListener('click', e => {
        const action = e.target.closest('[data-model-action]')?.dataset.modelAction;
        if (!action || !this.ready) return;
        if (action === 'toggle') this.rotating = !this.rotating;
        else {
          this.rotating = false;
          if (action === 'reset') {
            this.model.resetTurntableRotation(0);
            this.model.cameraOrbit = '0deg 87deg 2.55m';
          } else {
            const orbit = this.model.getCameraOrbit();
            const angle = orbit.theta + (action === 'left' ? -1 : 1) * Math.PI / 4;
            this.model.cameraOrbit = `${angle}rad ${orbit.phi}rad ${orbit.radius}m`;
          }
          if (this.motion.matches) this.model.jumpCameraToGoal();
        }
        this.syncRotation();
      }, options);
      this.retry.addEventListener('click', () => this.load(), options);
      this.motion.addEventListener('change', () => { if (this.motion.matches) this.pause(); }, options);
      document.addEventListener('visibilitychange', () => this.syncRotation(), options);
      this.observer = new IntersectionObserver(entries => {
        this.visible = entries[0].isIntersecting;
        if (this.visible && !this.ready && !this.failed) this.load();
        this.syncRotation();
      }, { threshold: 0.05 });
      this.observer.observe(this);
    }
    async load() {
      if (this.loading) return;
      this.loading = true;
      this.failed = false;
      this.retry.hidden = true;
      this.status.textContent = 'Loading 3D preview…';
      const signal = this.abort.signal;
      try {
        runtime ||= import(this.dataset.runtime).catch(error => { runtime = null; throw error; });
        await runtime;
        await customElements.whenDefined('model-viewer');
        if (signal.aborted) return;
        this.model.removeAttribute('src');
        this.model.setAttribute('src', this.model.dataset.src);
      } catch (error) {
        if (!signal.aborted) this.fail();
      }
    }
    fail() {
      this.loading = false;
      this.ready = false;
      this.failed = true;
      this.classList.remove('is-ready');
      this.controls.hidden = true;
      this.retry.hidden = false;
      this.status.textContent = '3D unavailable. Showing the collection preview.';
      this.syncRotation();
    }
    pause() { this.rotating = false; this.syncRotation(); }
    syncRotation() {
      this.model.autoRotate = Boolean(this.ready && this.rotating && this.visible && !document.hidden);
      this.toggle.setAttribute('aria-pressed', String(this.rotating));
      this.toggle.textContent = this.rotating ? 'Pause rotation' : 'Auto rotate';
    }
    disconnectedCallback() {
      this.abort?.abort();
      this.observer?.disconnect();
      if (this.model) this.model.autoRotate = false;
      this.abort = null;
      this.loading = false;
    }
  }
  customElements.define('hy-knit-viewer', KnitViewer);
})();
